/**
 * Defaults aligned with website-template ProductCard CSS fallbacks /
 * site-ui-config theme tokens.
 */
export const DEFAULT_ITEM_PREVIEW_THEME = {
  primaryColor: "#0A6BAB",
  secondaryColor: "#8FBF3D",
  primaryLight: "#E9F4FB",
  primaryDeep: "#0A628F",
  textColor: "#2a4a5c",
  mutedColor: "#738a99",
  lineColor: "#dde8ef",
  fontFamily:
    '"Plus Jakarta Sans", "Cairo", "Segoe UI", system-ui, sans-serif',
  fontDisplay:
    '"Sora", "Plus Jakarta Sans", "Cairo", system-ui, sans-serif',
  buttonBg: "#0A6BAB",
  buttonText: "#FFFFFF",
}

const lightenHex = (hex, amount = 0.85) => {
  const raw = String(hex || "").replace("#", "")
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return DEFAULT_ITEM_PREVIEW_THEME.primaryLight
  const num = parseInt(raw, 16)
  const r = (num >> 16) & 255
  const g = (num >> 8) & 255
  const b = num & 255
  const mix = channel => Math.round(channel + (255 - channel) * amount)
  return `#${[mix(r), mix(g), mix(b)]
    .map(v => v.toString(16).padStart(2, "0"))
    .join("")}`
}

/**
 * Same CSS custom properties as website-template `getThemeCssVariables`
 * (subset needed by ProductCard).
 */
export const buildItemPreviewThemeStyle = (theme = {}) => {
  const primaryColor =
    theme.primaryColor || DEFAULT_ITEM_PREVIEW_THEME.primaryColor
  const secondaryColor =
    theme.secondaryColor || DEFAULT_ITEM_PREVIEW_THEME.secondaryColor
  const primaryLight =
    theme.primaryLight || DEFAULT_ITEM_PREVIEW_THEME.primaryLight
  const primaryDeep =
    theme.primaryDeep || primaryColor || DEFAULT_ITEM_PREVIEW_THEME.primaryDeep
  const textColor = theme.textColor || DEFAULT_ITEM_PREVIEW_THEME.textColor
  const mutedColor = theme.mutedColor || DEFAULT_ITEM_PREVIEW_THEME.mutedColor
  const lineColor = theme.lineColor || DEFAULT_ITEM_PREVIEW_THEME.lineColor
  const fontFamily = theme.fontFamily || DEFAULT_ITEM_PREVIEW_THEME.fontFamily
  const fontDisplay =
    theme.fontDisplay || theme.fontFamily || DEFAULT_ITEM_PREVIEW_THEME.fontDisplay
  const buttonBg = theme.buttonBg || primaryColor
  const buttonText = theme.buttonText || DEFAULT_ITEM_PREVIEW_THEME.buttonText

  return {
    "--color-primary": primaryColor,
    "--color-secondary": secondaryColor,
    "--color-accent": secondaryColor,
    "--color-primary-light": primaryLight,
    "--color-primary-deep": primaryDeep,
    "--color-text": textColor,
    "--color-muted": mutedColor,
    "--color-line": lineColor,
    "--font-family": fontFamily,
    "--font-display": fontDisplay,
    "--btn-bg": buttonBg,
    "--btn-text": buttonText,
    fontFamily,
  }
}

export const extractThemeFromWebsiteConfig = config => {
  if (!config || typeof config !== "object") {
    return null
  }

  // Published website-config shape from GET public/website-config/:tenantId
  const theme =
    config.theme ||
    config.config?.theme ||
    config.data?.theme ||
    null

  // Legacy public theme endpoint nests colors under `website`
  const legacyWebsite =
    !theme && config.website && typeof config.website === "object"
      ? config.website
      : null

  const source = theme || legacyWebsite
  if (!source || typeof source !== "object") {
    return null
  }

  const primaryColor = source.primaryColor || source.primary
  if (!primaryColor) return null

  const buttons = source.buttons || config.theme?.buttons || {}

  return {
    primaryColor,
    secondaryColor:
      source.secondaryColor ||
      source.secondary ||
      DEFAULT_ITEM_PREVIEW_THEME.secondaryColor,
    primaryLight: source.primaryLight || lightenHex(primaryColor, 0.88),
    primaryDeep: source.primaryDeep || primaryColor,
    textColor: source.textColor || DEFAULT_ITEM_PREVIEW_THEME.textColor,
    mutedColor: source.mutedColor || DEFAULT_ITEM_PREVIEW_THEME.mutedColor,
    lineColor: source.lineColor || DEFAULT_ITEM_PREVIEW_THEME.lineColor,
    fontFamily: source.fontFamily || DEFAULT_ITEM_PREVIEW_THEME.fontFamily,
    fontDisplay:
      source.fontDisplay ||
      source.fontFamily ||
      DEFAULT_ITEM_PREVIEW_THEME.fontDisplay,
    buttonBg: buttons.backgroundColor || primaryColor,
    buttonText: buttons.textColor || DEFAULT_ITEM_PREVIEW_THEME.buttonText,
  }
}

/** Map portal tenant branding → preview theme when website theme is unavailable. */
export const extractThemeFromTenantBranding = branding => {
  if (!branding || typeof branding !== "object") return null
  const primaryColor =
    branding.buttons?.primaryBackgroundColor ||
    branding.sidebar?.iconColor ||
    ""
  if (!primaryColor) return null

  const fontId = branding.appearance?.fontFamily
  const fontStacks = {
    inter: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
    poppins: "'Poppins', 'Segoe UI', system-ui, -apple-system, sans-serif",
    "dm-sans": "'DM Sans', 'Segoe UI', system-ui, -apple-system, sans-serif",
    system:
      "'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  }
  const fontFamily = fontStacks[fontId] || fontStacks.inter

  return {
    primaryColor,
    secondaryColor:
      branding.buttons?.secondaryBackgroundColor ||
      DEFAULT_ITEM_PREVIEW_THEME.secondaryColor,
    primaryLight: lightenHex(primaryColor, 0.88),
    primaryDeep: primaryColor,
    textColor: branding.header?.textColor || DEFAULT_ITEM_PREVIEW_THEME.textColor,
    mutedColor: DEFAULT_ITEM_PREVIEW_THEME.mutedColor,
    lineColor: DEFAULT_ITEM_PREVIEW_THEME.lineColor,
    fontFamily,
    fontDisplay: fontFamily,
    buttonBg: primaryColor,
    buttonText: branding.buttons?.primaryTextColor || "#ffffff",
  }
}

const GOOGLE_FONT_MAP = {
  "Plus Jakarta Sans": "Plus+Jakarta+Sans:wght@400;500;600;700",
  Sora: "Sora:wght@400;500;600;700",
  Cairo: "Cairo:wght@400;500;600;700",
  Inter: "Inter:wght@400;500;600;700",
  Poppins: "Poppins:wght@400;500;600;700",
  "DM Sans": "DM+Sans:opsz,wght@9..40,400;500;600;700",
}

/** Ensure tenant website fonts are available in the portal document. */
export const ensurePreviewFontsLoaded = theme => {
  if (typeof document === "undefined" || !theme) return
  const stacks = `${theme.fontFamily || ""} ${theme.fontDisplay || ""}`
  const families = Object.keys(GOOGLE_FONT_MAP).filter(name =>
    stacks.includes(name)
  )
  if (!families.length) return

  const id = "item-preview-theme-fonts"
  const href = `https://fonts.googleapis.com/css2?${families
    .map(name => `family=${GOOGLE_FONT_MAP[name]}`)
    .join("&")}&display=swap`

  let link = document.getElementById(id)
  if (!link) {
    link = document.createElement("link")
    link.id = id
    link.rel = "stylesheet"
    document.head.appendChild(link)
  }
  if (link.getAttribute("href") !== href) {
    link.setAttribute("href", href)
  }
}
